import { useState } from 'react';
import { ChevronUp, ChevronDown, ArrowUpDown, ExternalLink, X, MessageCircle, Eye } from 'lucide-react';
import type { RisingCreator, RisingCreatorStatus } from '../../hooks/useRisingCreators';
import StatusBadge from './StatusBadge';
import SourceBadge from './SourceBadge';

interface CreatorsTableProps {
  creators: RisingCreator[];
  selectedIds: Set<string>;
  onSelectId: (id: string) => void;
  onSelectAll: () => void;
  onDetails: (creator: RisingCreator) => void;
  onContact: (creator: RisingCreator) => void;
  onReject: (creator: RisingCreator) => void;
  onBulkReject: () => void;
  onBulkAssign: (member: string) => void;
}

type SortKey = 'username' | 'followers_count' | 'growth_rate_30d' | 'engagement_rate' | 'rising_score' | 'first_seen_at';

function formatFollowers(n: number | null): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

const TEAM_MEMBERS = ['Hosni', 'Camille', 'Alexis'];

export default function CreatorsTable({
  creators,
  selectedIds,
  onSelectId,
  onSelectAll,
  onDetails,
  onContact,
  onReject,
  onBulkReject,
  onBulkAssign,
}: CreatorsTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('rising_score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sorted = [...creators].sort((a, b) => {
    const aVal = a[sortKey] ?? 0;
    const bVal = b[sortKey] ?? 0;
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3.5 h-3.5 text-blue-600" />
      : <ChevronDown className="w-3.5 h-3.5 text-blue-600" />;
  };

  const allSelected = creators.length > 0 && creators.every(c => selectedIds.has(c.id));

  return (
    <div>
      {selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-xl">
          <span className="text-sm font-semibold text-blue-700">{selectedIds.size} sélectionné(s)</span>
          <button
            onClick={onBulkReject}
            className="flex items-center gap-1.5 px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-semibold rounded-lg transition-colors"
          >
            <X className="w-3.5 h-3.5" /> Rejeter la sélection
          </button>
          <span className="text-xs text-blue-500">Assigner à :</span>
          {TEAM_MEMBERS.map(m => (
            <button
              key={m}
              onClick={() => onBulkAssign(m)}
              className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-semibold rounded-lg transition-colors"
            >
              {m}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={onSelectAll}
                    className="rounded accent-blue-600"
                  />
                </th>
                {([
                  { key: 'username', label: 'Créateur' },
                  { key: 'platform', label: 'Plateforme', noSort: true },
                  { key: 'followers_count', label: 'Followers' },
                  { key: 'growth_rate_30d', label: 'Croissance 30j' },
                  { key: 'engagement_rate', label: 'Engagement' },
                  { key: 'rising_score', label: 'Score' },
                  { key: 'source', label: 'Source', noSort: true },
                  { key: 'status', label: 'Statut', noSort: true },
                  { key: 'first_seen_at', label: 'Détecté' },
                ] as const).map(col => (
                  <th key={col.key} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {col.noSort ? (
                      col.label
                    ) : (
                      <button
                        onClick={() => handleSort(col.key as SortKey)}
                        className="flex items-center gap-1.5 hover:text-gray-900 transition-colors"
                      >
                        {col.label}
                        <SortIcon col={col.key as SortKey} />
                      </button>
                    )}
                  </th>
                ))}
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.map(creator => (
                <tr
                  key={creator.id}
                  className={`hover:bg-blue-50 transition-colors ${selectedIds.has(creator.id) ? 'bg-blue-50' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(creator.id)}
                      onChange={() => onSelectId(creator.id)}
                      onClick={e => e.stopPropagation()}
                      className="rounded accent-blue-600"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <img
                        src={creator.profile_picture_url || 'https://via.placeholder.com/32'}
                        alt={creator.username}
                        className="w-7 h-7 rounded-full object-cover border border-gray-200"
                        onError={e => { e.currentTarget.src = 'https://via.placeholder.com/32'; }}
                      />
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">{creator.full_name || creator.username}</div>
                        <div className="text-xs text-gray-500">@{creator.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 capitalize">{creator.platform}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{formatFollowers(creator.followers_count)}</td>
                  <td className="px-4 py-3">
                    {creator.growth_rate_30d !== null ? (
                      <span className={`text-sm font-semibold ${(creator.growth_rate_30d || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {(creator.growth_rate_30d || 0) >= 0 ? '+' : ''}{creator.growth_rate_30d?.toFixed(1)}%
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-semibold ${
                      (creator.engagement_rate || 0) >= 4 ? 'text-green-600' :
                      (creator.engagement_rate || 0) >= 2 ? 'text-amber-600' : 'text-gray-600'
                    }`}>
                      {creator.engagement_rate?.toFixed(2) || '—'}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-bold text-base ${
                      creator.rising_score >= 70 ? 'text-green-600' :
                      creator.rising_score >= 40 ? 'text-amber-600' : 'text-gray-500'
                    }`}>
                      {creator.rising_score}
                    </span>
                  </td>
                  <td className="px-4 py-3"><SourceBadge source={creator.source} /></td>
                  <td className="px-4 py-3"><StatusBadge status={creator.status} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {new Date(creator.first_seen_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => onDetails(creator)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Voir détails"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onContact(creator)}
                        className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                        title="Approcher"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </button>
                      {creator.profile_url && (
                        <a
                          href={creator.profile_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Voir profil"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button
                        onClick={() => onReject(creator)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Rejeter"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sorted.length === 0 && (
            <div className="py-16 text-center text-gray-400 text-sm">Aucun créateur trouvé</div>
          )}
        </div>
      </div>
    </div>
  );
}
